// /app/frontend/src/pages/DashboardCommercialPage.js
/**
 * Dashboard spécifique pour le rôle Commercial
 * Affiche les commissions, événements associés, et métriques clés
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Euro,
  TrendingUp,
  Calendar,
  Users,
  CheckCircle,
  Clock,
  AlertCircle,
  Loader2,
  BarChart3,
  Target,
  Briefcase,
  ArrowUpRight,
  ArrowDownRight,
  Phone,
  Mail,
  MapPin,
  Building2,
  Percent,
  PiggyBank,
  CalendarDays,
  ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

const DashboardCommercialPage = () => {
  const token = localStorage.getItem('token');
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [periode, setPeriode] = useState('mois');
  
  // User info
  const [userInfo, setUserInfo] = useState(null);
  const [commercialId, setCommercialId] = useState(null);
  
  // Stats
  const [commissionStats, setCommissionStats] = useState(null);
  const [recentCommissions, setRecentCommissions] = useState([]);
  const [recentEvents, setRecentEvents] = useState([]);
  const [crmStats, setCrmStats] = useState({ clients: 0, leads: 0 });
  
  const headers = { Authorization: `Bearer ${token}` };

  // Get user info and find associated commercial
  const fetchUserInfo = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/auth/me`, { headers });
      setUserInfo(res.data);
      
      // Try to find commercial by user email
      const commerciauxRes = await axios.get(`${API}/api/commissions/commerciaux`, { headers });
      const commerciaux = commerciauxRes.data || [];
      const myCommercial = commerciaux.find(c => 
        c.email?.toLowerCase() === res.data.email?.toLowerCase() ||
        c.nom?.toLowerCase() === res.data.nom?.toLowerCase()
      );
      
      if (myCommercial) {
        setCommercialId(myCommercial.id);
      }
      
      return myCommercial?.id;
    } catch (error) {
      console.error('Error fetching user info:', error);
      return null;
    }
  }, [token]);

  // Fetch commission stats
  const fetchCommissionStats = useCallback(async (commId) => {
    try {
      const params = commId ? `?commercial_id=${commId}&periode=${periode}` : `?periode=${periode}`;
      const res = await axios.get(`${API}/api/commissions/dashboard${params}`, { headers });
      setCommissionStats(res.data);
    } catch (error) {
      console.error('Error fetching commission stats:', error);
    }
  }, [token, periode]);

  // Fetch recent commissions
  const fetchRecentCommissions = useCallback(async (commId) => {
    try {
      const params = commId ? `?commercial_id=${commId}` : '';
      const res = await axios.get(`${API}/api/commissions/${params}`, { headers });
      setRecentCommissions((res.data.commissions || []).slice(0, 5));
    } catch (error) {
      console.error('Error fetching commissions:', error);
    }
  }, [token]);

  // Fetch recent events
  const fetchRecentEvents = useCallback(async () => {
    try {
      // Get events from both unitaire and organised
      const [unitairesRes, organisedRes] = await Promise.all([
        axios.get(`${API}/api/events`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/api/organised-events`, { headers }).catch(() => ({ data: [] }))
      ]);
      
      const unitaires = (unitairesRes.data || []).map(e => ({ ...e, type: 'unitaire' }));
      const organised = (organisedRes.data || []).map(e => ({ ...e, type: 'multiple' }));
      
      // Combine and sort by date
      const allEvents = [...unitaires, ...organised]
        .sort((a, b) => new Date(b.created_at || b.date_debut) - new Date(a.created_at || a.date_debut))
        .slice(0, 10);
      
      setRecentEvents(allEvents);
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  }, [token]);

  // Fetch CRM stats
  const fetchCrmStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/crm/clients`, { headers });
      const clients = res.data || [];
      
      const clientCount = clients.filter(c => c.statut === 'client' || c.type === 'client').length;
      const leadCount = clients.filter(c => c.statut === 'lead' || c.type === 'lead' || !c.statut).length;
      
      setCrmStats({ clients: clientCount, leads: leadCount });
    } catch (error) {
      console.error('Error fetching CRM stats:', error);
    }
  }, [token]);

  // Initial load
  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      const commId = await fetchUserInfo();
      await Promise.all([
        fetchCommissionStats(commId),
        fetchRecentCommissions(commId),
        fetchRecentEvents(),
        fetchCrmStats()
      ]);
      setLoading(false);
    };
    loadAll();
  }, []);

  // Reload on period change
  useEffect(() => {
    if (commercialId !== null) {
      fetchCommissionStats(commercialId);
    }
  }, [periode, commercialId, fetchCommissionStats]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR');
  };

  const getStatutBadge = (statut) => {
    switch (statut) {
      case 'confirme':
      case 'valide':
        return <Badge className="bg-green-100 text-green-700">Confirmé</Badge>;
      case 'en_attente':
        return <Badge className="bg-amber-100 text-amber-700">En attente</Badge>;
      case 'annule':
        return <Badge className="bg-red-100 text-red-700">Annulé</Badge>;
      default:
        return <Badge variant="secondary">{statut || 'N/A'}</Badge>;
    }
  };

  const getCommissionStatutBadge = (statut) => {
    switch (statut) {
      case 'paye':
        return <Badge className="bg-green-100 text-green-700"><CheckCircle className="w-3 h-3 mr-1" />Payé</Badge>;
      case 'partiel':
        return <Badge className="bg-amber-100 text-amber-700"><Clock className="w-3 h-3 mr-1" />Partiel</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-700"><AlertCircle className="w-3 h-3 mr-1" />En attente</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-[#8B1538]" />
      </div>
    );
  }

  const totaux = commissionStats?.totaux || {};
  const parCommercial = commissionStats?.par_commercial || [];
  const myStats = parCommercial.length > 0 ? parCommercial[0] : null;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Briefcase className="h-6 w-6 text-[#8B1538]" />
            Tableau de Bord Commercial
          </h1>
          <p className="text-gray-500 mt-1">
            Bienvenue, {userInfo?.prenom || userInfo?.nom || 'Commercial'}
          </p>
        </div>
        <div className="flex items-center gap-4">
          <Select value={periode} onValueChange={setPeriode}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="mois">Ce mois</SelectItem>
              <SelectItem value="trimestre">Ce trimestre</SelectItem>
              <SelectItem value="annee">Cette année</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">CA Généré (HT)</p>
                <p className="text-2xl font-bold">{formatCurrency(totaux.total_ca_ht)}</p>
                <p className="text-xs text-gray-400 mt-1">
                  {periode === 'mois' ? 'Ce mois' : periode === 'trimestre' ? 'Ce trimestre' : 'Cette année'}
                </p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <TrendingUp className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-[#8B1538]">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Commissions Gagnées</p>
                <p className="text-2xl font-bold text-[#8B1538]">{formatCurrency(totaux.total_commission)}</p>
                <p className="text-xs text-gray-400 mt-1">
                  {totaux.nb_commissions || 0} commission(s)
                </p>
              </div>
              <div className="p-3 bg-[#8B1538]/10 rounded-full">
                <Euro className="h-6 w-6 text-[#8B1538]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-green-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Encaissé</p>
                <p className="text-2xl font-bold text-green-600">{formatCurrency(totaux.total_paye)}</p>
                <p className="text-xs text-gray-400 mt-1">
                  {totaux.total_commission > 0 
                    ? `${Math.round((totaux.total_paye / totaux.total_commission) * 100)}% du total`
                    : '0%'
                  }
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <PiggyBank className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">En Attente</p>
                <p className="text-2xl font-bold text-amber-600">{formatCurrency(totaux.total_restant)}</p>
                <p className="text-xs text-gray-400 mt-1">
                  {totaux.nb_en_attente || 0} à encaisser
                </p>
              </div>
              <div className="p-3 bg-amber-100 rounded-full">
                <Clock className="h-6 w-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Performance by Type */}
      {myStats && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Target className="h-5 w-5 text-blue-500" />
                Vente Libre
              </CardTitle>
              <CardDescription>Taux: 7%</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-2xl font-bold">{myStats.vente_libre?.count || 0}</p>
                  <p className="text-xs text-gray-500">Événements</p>
                </div>
                <div>
                  <p className="text-2xl font-bold">{formatCurrency(myStats.vente_libre?.ca || 0)}</p>
                  <p className="text-xs text-gray-500">CA HT</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-blue-600">{formatCurrency(myStats.vente_libre?.commission || 0)}</p>
                  <p className="text-xs text-gray-500">Commission</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Building2 className="h-5 w-5 text-purple-500" />
                Privatisation
              </CardTitle>
              <CardDescription>Taux: 10%</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-2xl font-bold">{myStats.privatisation?.count || 0}</p>
                  <p className="text-xs text-gray-500">Événements</p>
                </div>
                <div>
                  <p className="text-2xl font-bold">{formatCurrency(myStats.privatisation?.ca || 0)}</p>
                  <p className="text-xs text-gray-500">CA HT</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-purple-600">{formatCurrency(myStats.privatisation?.commission || 0)}</p>
                  <p className="text-xs text-gray-500">Commission</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Commissions */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Percent className="h-5 w-5 text-[#8B1538]" />
                Dernières Commissions
              </CardTitle>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate('/commissions')}
              >
                Voir tout <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {recentCommissions.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Événement</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">Commission</TableHead>
                    <TableHead>Statut</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recentCommissions.map((c) => (
                    <TableRow key={c.id}>
                      <TableCell className="text-sm">{formatDate(c.created_at)}</TableCell>
                      <TableCell className="max-w-[200px] truncate">{c.event_titre}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {c.type_prestation === 'privatisation' ? 'Privé' : 'VL'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        {formatCurrency(c.montant_commission)}
                      </TableCell>
                      <TableCell>{getCommissionStatutBadge(c.statut)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Euro className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Aucune commission pour cette période</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Stats CRM */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-[#8B1538]" />
              CRM
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div 
              className="p-4 bg-blue-50 rounded-lg cursor-pointer hover:bg-blue-100 transition-colors"
              onClick={() => navigate('/crm')}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600">Clients</p>
                  <p className="text-3xl font-bold text-blue-700">{crmStats.clients}</p>
                </div>
                <CheckCircle className="h-8 w-8 text-blue-500" />
              </div>
            </div>
            
            <div 
              className="p-4 bg-amber-50 rounded-lg cursor-pointer hover:bg-amber-100 transition-colors"
              onClick={() => navigate('/crm')}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-amber-600">Leads</p>
                  <p className="text-3xl font-bold text-amber-700">{crmStats.leads}</p>
                </div>
                <Target className="h-8 w-8 text-amber-500" />
              </div>
            </div>

            <Button 
              className="w-full bg-[#8B1538] hover:bg-[#6B1028]"
              onClick={() => navigate('/crm')}
            >
              Gérer le CRM
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Recent Events */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <CalendarDays className="h-5 w-5 text-[#8B1538]" />
              Événements Récents
            </CardTitle>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate('/planning-unifie')}
            >
              Voir le planning <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {recentEvents.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {recentEvents.slice(0, 6).map((event) => (
                <div 
                  key={event.id}
                  className="p-4 border rounded-lg hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => navigate('/planning-unifie')}
                >
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-medium text-gray-900 truncate flex-1">{event.titre}</h4>
                    {getStatutBadge(event.statut)}
                  </div>
                  <div className="space-y-1 text-sm text-gray-500">
                    <p className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {formatDate(event.date_debut || event.dates?.[0]?.date)}
                    </p>
                    <p className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      {event.lieu || 'Non défini'}
                    </p>
                    {event.client && (
                      <p className="flex items-center gap-1">
                        <Building2 className="w-4 h-4" />
                        {event.client}
                      </p>
                    )}
                  </div>
                  <div className="mt-3 pt-2 border-t flex items-center justify-between text-xs">
                    <Badge variant="outline">
                      {event.type === 'multiple' ? 'Multiple' : 'Unitaire'}
                    </Badge>
                    <span className="text-gray-400">
                      {event.type_prestation === 'privatisation' ? 'Privatisation' : 'Vente libre'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Calendar className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Aucun événement récent</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Actions Rapides</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Button 
              variant="outline" 
              className="h-20 flex-col gap-2"
              onClick={() => navigate('/planning-unifie')}
            >
              <Calendar className="h-6 w-6 text-[#8B1538]" />
              <span>Planning</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-20 flex-col gap-2"
              onClick={() => navigate('/crm')}
            >
              <Users className="h-6 w-6 text-[#8B1538]" />
              <span>CRM</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-20 flex-col gap-2"
              onClick={() => navigate('/annuaire-clients')}
            >
              <Building2 className="h-6 w-6 text-[#8B1538]" />
              <span>Annuaire</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-20 flex-col gap-2"
              onClick={() => navigate('/commissions')}
            >
              <Euro className="h-6 w-6 text-[#8B1538]" />
              <span>Commissions</span>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardCommercialPage;
